import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RegionKey, RegionSelectorService } from '@dashboard/app/services/region-selector.service';
import { AuthTemplateModule } from '@go-modules/components/auth-template/auth-template.module';
import { RouterService } from '@go-modules/services/router/router.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-region-selector',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		AuthTemplateModule
	],
	templateUrl: './region-selector.component.html',
	styleUrls: ['./region-selector.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegionSelectorComponent implements OnInit, OnDestroy {
	public readonly regionOptions = this.regionSelectorService.getOptionsForEnvironment();
	public showRegionSelector: boolean = false;
	public canDismiss: boolean = true;

	private destroy$ = new Subject<void>();

	constructor (
		private regionSelectorService: RegionSelectorService,
		private routerService: RouterService,
		private cdr: ChangeDetectorRef,
		@Inject('Window') private window: Window
	) {}

	public ngOnInit (): void {
		this.regionSelectorService.showRegionSelector$
			.pipe(takeUntil(this.destroy$))
			.subscribe((show) => {
				this.showRegionSelector = show;
				this.cdr.markForCheck();
			});

		this.regionSelectorService.canDismissRegionSelector$
			.pipe(takeUntil(this.destroy$))
			.subscribe((canDismiss) => {
				this.canDismiss = canDismiss;
				this.cdr.markForCheck();
			});

		const queryParams = this.routerService.getCurrentQueryParams();
		const regionState = this.regionSelectorService.initializeRegionSelectorState(queryParams);

		if (regionState.shouldRedirect && regionState.redirectUrl) {
			this.regionSelectorService.setIsRedirecting(true);
			this.regionSelectorService.setShowRegionSelector(false);
			this.window.location.replace(regionState.redirectUrl);
			return;
		}

		this.cdr.markForCheck();
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public selectRegion (region: RegionKey): void {
		const flow = this.getFlowFromPath();
		const result = this.regionSelectorService.handleRegionSelectionWithState(
			region,
			flow
		);

		if (result.navigated) {
			return;
		}

		this.cdr.markForCheck();
	}

	public close (): void {
		this.regionSelectorService.dismissRegionSelector();
		this.cdr.markForCheck();
	}

	private getFlowFromPath (): 'login' | 'signup' {
		const path = this.window.location.pathname || '';
		return path.includes('/signup') ? 'signup' : 'login';
	}
}
